import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import history from '../history';
import Cookies from 'universal-cookie';

import BlockbriefProductPackage from './components/products/blockbrief/BlockbriefProductPackage';
import CommercialProductPackage from './components/products/commercial/CommercialProductPackage';
import CordellConnectProductPackage from './components/products/cordellConnect/CordellConnectProductPackage';
import CordellEstimatorProductPackage from './components/products/cordellEstimator/CordellEstimatorProductPackage';
import RPPProductPackage from './components/products/rpp/RPPProductPackage';

import {
  analyticsPageView,
  trackError,
  trackPackageSelection
} from '../services/v2/segmentAnalytics';
import { parseUrlForProductParams, productSwitch } from '../services/v2/productsService';

import {
  changeIndustry,
  fetchAffiliatesDetails,
  fetchProduct,
  setCouponCodeForCart,
  setPackage
} from '../actions/packageSelection';
import { applyCoupon, deleteOrder, fetchCart, saveCartItem } from '../actions/cart';
import { userExpirySession } from '../actions/login';

/**
 * RegistrationPage.
 * Page for customer registration.
 *
 * @author  Manny
 * @param   {Object}      props.location  Window.location Object.
 * @param   {Object}      props.match     Object that contains match.params.
 * @returns {JSX.Element} The component markup.
 */

const PackageSelectionPage = ({ location, match }) => {
  const coupon = useSelector((state) => state.packageSelection.coupon);
  const couponCode = useSelector((state) => state.packageSelection.couponCode);
  const loading = useSelector((state) => state.packageSelection.loading);
  const productFetchFailure = useSelector((state) => state.packageSelection.productFetchFailure)
  const order = useSelector((state) => state.cart.order);
  const saveCartItemLoader = useSelector((state) => state.cart.saveCartItemLoader)
  const product = useSelector((state) => state.packageSelection);
  const [selectedIndustry, setSelectedIndustry] = useState('real_estate_agencies_and_valuers');
  const [selectedPackage, setSelectedPackage] = useState('');
  const [renderLoading, setRenderLoading] = useState(true);
  const [industryCookie, setIndustryCookie] = useState(false);

  const dispatch = useDispatch();

  // CONSTANTS
  const FRIENDLY_NAME = match.params.friendly_name;
  const QUOTE_SERVICE_TYPE = product.productsConfig[FRIENDLY_NAME]?.quoteServiceType || null;

  // componentDidMount useEffect
  useEffect(() => {
    setRenderLoading(true);
    history.listen((newLocation) => {
      if (/^\/product\/[a-zA-Z\-=?]+$/.test(newLocation.pathname)) {
        window.location.reload();
      }
    });

    analyticsPageView('Package Selection');

    window.scrollTo(0, 0);
    dispatch(userExpirySession());

    // set Cookie for chat live
    const cookies = new Cookies();
    let { methodParam, couponParam, packageParam } = parseUrlForProductParams(location);

    if (FRIENDLY_NAME === 'rp-data-professional') {
        cookies.set('industry', 'real_estate_agencies_and_valuers');
    } else if (selectedIndustry) cookies.set('industry', selectedIndustry);
    else cookies.remove('industry');


    dispatch(setCouponCodeForCart(couponParam));

    if (!couponParam && coupon && Object.keys(coupon).length > 0) couponParam = coupon.code;

    dispatch(setCouponCodeForCart(couponParam))


    if (FRIENDLY_NAME !== 'bundle') {
      dispatch(fetchProduct(FRIENDLY_NAME, methodParam)).then(() => {
        if (couponParam) {
          dispatch(fetchAffiliatesDetails(couponParam, packageParam))
        }
      })
    }
  }, []);

  useEffect(() => {
    let { methodParam, couponParam, industryParam, packageParam } = parseUrlForProductParams(location);
    if (couponParam && coupon && !productFetchFailure) {
      onRedirectToConfig(packageParam, industryParam);
      handleFetchProduct(methodParam);
    } else if (couponParam && !productFetchFailure) {
      checkBundleOrder(couponParam);
      handleFetchProduct(methodParam);
    } else if (couponParam && productFetchFailure) return
  }, [coupon, productFetchFailure])

  useEffect(() => {
    let { couponParam, industryParam, packageParam } = parseUrlForProductParams(location);
    if(product.productsConfig && !couponParam) {
      if(product.productsConfig[FRIENDLY_NAME]?.status === 'INACTIVE') return;
      if (packageParam === null) onIndustrySelection('realEstate', null);
      else if (packageParam !== null) {
        if(FRIENDLY_NAME === 'rp-data-professional' && packageParam == 'trial') return;
        if(!industryParam?.includes('mortgageFinance', 'investors', 'trades'))
          onRedirectToConfig(packageParam, industryParam);
        else onIndustrySelection('realEstate', null);
      }
      else if (FRIENDLY_NAME === 'residential') onSelectPackage(null, 'residential');
      else if (FRIENDLY_NAME === 'investors-premium') onSelectPackage(null, 'investors_premium');
      else if (FRIENDLY_NAME === 'property-developers-construction')
        onSelectPackage(null, 'property_developers_and_construction');
      else if (FRIENDLY_NAME === 'emi-premium') onSelectPackage(null, 'premium');
    }
    if (product.productFetchFailure && product.failureMessage !== null)
      trackError('Fetch Product', null, product.failureMessage, '404');
  }, [dispatch, product.productsConfig, selectedPackage]);

  const handleFetchProduct = (newMethodParam) => {
    if (FRIENDLY_NAME == 'bundle') return;

    dispatch(fetchProduct(FRIENDLY_NAME, newMethodParam));
  };

  const addTrialPackage = (industryKey) => {
    let configurationBlob = product.productsConfig[FRIENDLY_NAME].quoteConfigurationTemplate.configuration;
    if (QUOTE_SERVICE_TYPE === 'RPPRealEstate') {
      configurationBlob.industry = industryKey;
      configurationBlob.product_id = product.productsConfig[FRIENDLY_NAME].id;
      configurationBlob.package = 'trial';
      configurationBlob.number_of_users = 1;
      configurationBlob.geography.states = [];
      configurationBlob.geography.states.push('Limited');
    } else if (QUOTE_SERVICE_TYPE === 'CordellConnect') {
      configurationBlob.geography.state_type = 'limited';
      configurationBlob.product_id = product.productsConfig[FRIENDLY_NAME].id;
      configurationBlob.package = 'trial';
      configurationBlob.number_of_users = 1;
    }

    const cartItem = {
      configuration: configurationBlob,
      product_id: configurationBlob.product_id
    };

    if (order && order !== null && Object.keys(order).length > 0)
      dispatch(deleteOrder(cartItem));
    else {
      dispatch(setPackage(industryKey, 'trial', FRIENDLY_NAME));
      setSelectedIndustry(industryKey);
      setSelectedPackage('trial');
      if (!saveCartItemLoader && cartItem.product_id)
        dispatch(saveCartItem(cartItem));
    }
  };

  const addPackageToCart = (newProductId, packageKey) => {
    let industryKey = null;

    let configurationBlob = product.productsConfig[FRIENDLY_NAME].quoteConfigurationTemplate.configuration;
    if (product.productsConfig[FRIENDLY_NAME].quoteServiceType === 'Blockbrief') {
      configurationBlob.package = packageKey;
      configurationBlob.number_of_users = packageKey === 'premier' ? 1 : 4;
      configurationBlob.product_id = newProductId;
    } else if (product.productsConfig[FRIENDLY_NAME].quoteServiceType === 'ReportBundle') {
      configurationBlob.packageName = packageKey;
      configurationBlob.product_id = newProductId;
    } else if (packageKey === 'basic1UN') {
      configurationBlob.package = packageKey;
      configurationBlob.number_of_users = 1;
      configurationBlob.geography.national = true;
      configurationBlob.product_id = newProductId;
      configurationBlob.industry = 'property_developers_trades_services';
      industryKey = 'property_developers_trades_services';
    } else if (packageKey === 'premium1UN' || packageKey === 'premium1UNNAB'){
      configurationBlob.package = packageKey;
      configurationBlob.number_of_users = 1;
      configurationBlob.product_id = newProductId;
      configurationBlob.geography.national = true;
      configurationBlob.industry = 'real_estate_agencies_and_valuers';
      industryKey = 'real_estate_agencies_and_valuers';
    }
    if (couponCode) configurationBlob.coupon_code = couponCode

    const cartItem = {
      configuration: configurationBlob,
      product_id: newProductId
    };
    if (order && order !== null && Object.keys(order).length > 0)
      dispatch(deleteOrder(cartItem));
    else {
      dispatch(setPackage(industryKey, packageKey, FRIENDLY_NAME));
      setSelectedIndustry(industryKey);
      setSelectedPackage(packageKey);
      if (!saveCartItemLoader) dispatch(saveCartItem(cartItem));
    }
  };


  const checkBundleOrder = (coupon) => {
    if (FRIENDLY_NAME !== 'bundle') return;
    if (coupon) {
      const ids = coupon.productIds;
      if (ids !== null) {
        for (let id = 0; id < ids.length; id++) {
          getProductConfig(ids[id], id, ids);
        }
      }
    }
  };

  const trackPackageSelectionAnalytics = (industryKey, packageKey) => {
    if (product.productsConfig[FRIENDLY_NAME]) {
      const productName = product.productsConfig[FRIENDLY_NAME].productName;
      const configuration = product.productsConfig[FRIENDLY_NAME].configuration;
      if (configuration) {
        if (industryKey) {
          if (!Object.prototype.hasOwnProperty.call(configuration.industries[industryKey].packages, packageKey)) return
        } else {
          if (!Object.prototype.hasOwnProperty.call(configuration.packages, packageKey)) return
        }
        const packageTitle = industryKey
          ? configuration.industries[industryKey].packages[packageKey].title
          : configuration.packages[packageKey].title;

        let industryTitle;
        if (configuration.industries) industryTitle = configuration.industries[industryKey].title;
        trackPackageSelection(productName, industryTitle, packageTitle);
      }
    }
  };

  const onSelectPackage = (industryKey, packageKey) => {
    if (product.productsConfig[FRIENDLY_NAME]) {
      trackPackageSelectionAnalytics(industryKey, packageKey);

      if (packageKey === 'trial')
        addTrialPackage(industryKey);
      else if (
        product.productsConfig[FRIENDLY_NAME].quoteServiceType === 'Blockbrief' ||
        (product.productsConfig[FRIENDLY_NAME].quoteServiceType === 'ReportBundle' &&
          packageKey !== 'investors_premium') ||
        (product.productsConfig[FRIENDLY_NAME].quoteServiceType === 'RPPRealEstate' && packageKey === 'basic1UN') ||
        (product.productsConfig[FRIENDLY_NAME].quoteServiceType === 'EarlyMarketIndicator' && packageKey === 'premium') ||
        (product.productsConfig[FRIENDLY_NAME].quoteServiceType === 'RPPRealEstate' && (packageKey === 'premium1UN' || packageKey === 'premium1UNNAB'))
      ) {
        addPackageToCart(product.productsConfig[FRIENDLY_NAME].id, packageKey);
      } else {
        dispatch(setPackage(industryKey, packageKey, FRIENDLY_NAME));
        setSelectedIndustry(industryKey);
        setSelectedPackage(packageKey);
        history.push(`/product_config/${product.productsConfig[FRIENDLY_NAME].id}`);
      }
    }
  };

  const onRedirectToConfig = (packageName, industryName) => {
    if( packageName === 'one_month'&& industryName == 'investors'){
      setTimeout(() => {
        handleChangeIndustry('real_estate_agencies_and_valuers', packageName);
        onSelectPackage('real_estate_agencies_and_valuers', packageName);
        }, 1000);
    }else if (industryName == 'mortgageFinance' ||
      industryName == 'trades' ||
      industryName == 'investors'
      ){
        onIndustrySelection('realEstate', null);
      }
    else if (
      packageName !== undefined &&
      FRIENDLY_NAME === 'rp-data-professional'
    ) {
      if(packageName === 'premium1UN' || packageName === 'premium1UNNAB'){
        setTimeout(() => {
          onSelectPackage('real_estate_agencies_and_valuers', packageName);
        }, 1000);
      }else if(packageName === 'base' ||
          packageName === 'lite' ||
          packageName === 'premium' ||
          packageName === 'pro' ||
          packageName === 'trial'||
          packageName === 'one_month'
        ){
          setTimeout(() => {
          handleChangeIndustry('real_estate_agencies_and_valuers', packageName);
          onSelectPackage('real_estate_agencies_and_valuers', packageName);
          }, 1000);
      }
    } else if (packageName !== undefined) {
      if (FRIENDLY_NAME === 'commercial') {
        if (
          packageName === 'cityscope' ||
          packageName === 'lease_expiry_diary' ||
          packageName === 'combo'
        ) {
          setTimeout(() => {
            onSelectPackage(null, packageName);
          }, 1000);
        }
      } else if (FRIENDLY_NAME === 'cordell-connect') {
        if (
          packageName === 'lite'
        ) {
          setTimeout(() => {
            onSelectPackage(null, packageName);
          }, 1000);
        }
      } else if (FRIENDLY_NAME === 'blockbrief') {
        if (packageName === 'premier' || packageName === 'premier_pro') {
          setTimeout(() => {
            onSelectPackage(null, packageName);
          }, 1000);
        }
      } else if (FRIENDLY_NAME === 'cordell-costings') {
        if (packageName === 'cordell_estimator_platinum_lite') {
          setTimeout(() => {
            onSelectPackage(null, packageName);
          }, 1000);
        }
      }
    }
  };

  const onIndustrySelection = (industry) => {
    let industryKey = null;
    if (FRIENDLY_NAME === 'rp-data-professional') {
      industryKey = 'real_estate_agencies_and_valuers';
      setTimeout(() => {
        if (industryKey && product.productsConfig[FRIENDLY_NAME]) {
          const packages = product.productsConfig[FRIENDLY_NAME].configuration.industries[industryKey].packages;
          const packageKey = Object.keys(packages)[Object.keys(packages).length - 1];
          handleChangeIndustry(industryKey, packageKey);
          setIndustryCookie(true);
        }
      }, 1000); // the size of productsConfig requires a buffer timer
    }
  };

  const getProductConfig = (value, index, array) => {
    let details = {
      industry: null,
      packageName: null,
      quoteServiceType: null,
      id: value
    };
    switch (value) {
      case 1:
        details.industry = 'investors';
        details.packageName = 'basic';
        details.quoteServiceType = 'RPPRealEstate';
        addProductPackageToCart(details, 'rp-data-professional', index, array);
        break;
      case 4:
        details.packageName = 'premier';
        details.quoteServiceType = 'Blockbrief';
        addProductPackageToCart(details, 'blockbrief', index, array);
        break;
      case 8:
        details.packageName = 'investors_premium';
        details.quoteServiceType = 'ReportBundle';
        addProductPackageToCart(details, 'investors-premium', index, array);
        break;
      default:
        break;
    }
  };

  const addProductPackageToCart = (value, friendlyName, index, array) => {
    let cartItem = null;
    dispatch(fetchProduct(friendlyName)).then(() => {
      dispatch(setPackage(value.industry, value.packageName, friendlyName));

      cartItem = productSwitch(value);

      if (order && order !== null && Object.keys(order).length > 0) {
        dispatch(deleteOrder('')).then(() => {
          if ( !saveCartItemLoader) dispatch(saveCartItem(cartItem, 'bundle'));
        });
      } else if ( !saveCartItemLoader) {
        dispatch(saveCartItem(cartItem, 'bundle')).then(() => {
          if (index === array.length - 1) {
            history.push(`/product_config/${array[0]}`);
            dispatch(fetchCart()).then(() => {
              dispatch(applyCoupon(couponCode));
            });
          }
        });
      }
    });
  };

  const handleChangeIndustry = (industryKey, packageKey) => {
    setTimeout(() => {
      setSelectedIndustry(industryKey);
      setSelectedPackage(packageKey);
      dispatch(changeIndustry(industryKey, packageKey));
    }, 1000); // the size of productsConfig requires a buffer timer
  };

  const productId = product.productsConfig[FRIENDLY_NAME]?.id || null;

  useEffect(() => {
    if (renderLoading && window.location.href.includes('rp-data-professional')) {
      setTimeout(() => {
        setRenderLoading(false);
      }, 2500); // allow other industry packages to be loaded for rpp
    } else
      setRenderLoading(false);
  }, [renderLoading]);

  return (
    <div>
      {!loading && !renderLoading && (
        <div>
          {product.productFetchFailure && product.failureMessage !== null && (
            <div className='off-white-bg'>
              <div className='container'>
                <div className='row no-margin'>
                  <div className='col-sm-12'>
                    <div
                      className='row white-bg cart-container margin-bottom-100 margin-top-100'
                      style={{ fontWeight: '600' }}
                    >
                      <br />
                      <div className='cart-item-header text-center red-text'>
                        {' '}
                        {product.failureMessage}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {QUOTE_SERVICE_TYPE === 'RPPRealEstate' && (
            <RPPProductPackage
              changeIndustry={handleChangeIndustry}
              coupon={coupon}
              industryCookie={industryCookie}
              method={product.productsConfig[FRIENDLY_NAME].method}
              onSelectPackage={onSelectPackage}
              product={product}
              productId={productId}
              productsConfig={product.productsConfig[FRIENDLY_NAME].configuration}
              selectedIndustry={selectedIndustry}
              selectedPackage={selectedPackage}
            />
          )}
          {QUOTE_SERVICE_TYPE === 'Commercial' && (
            <CommercialProductPackage
              coupon={coupon}
              onSelectPackage={onSelectPackage}
              productId={productId}
              productsConfig={product.productsConfig[FRIENDLY_NAME].configuration}
            />
          )}
          {QUOTE_SERVICE_TYPE === 'CordellConnect' && (
            <CordellConnectProductPackage
              coupon={coupon}
              method={product.productsConfig[FRIENDLY_NAME].method}
              onSelectPackage={onSelectPackage}
              productId={productId}
              productsConfig={product.productsConfig[FRIENDLY_NAME].configuration}
            />
          )}
          {QUOTE_SERVICE_TYPE === 'Blockbrief' && (
            <BlockbriefProductPackage
              coupon={coupon}
              onSelectPackage={onSelectPackage}
              productId={productId}
              productsConfig={product.productsConfig[FRIENDLY_NAME].configuration}
            />
          )}
          {QUOTE_SERVICE_TYPE === 'CordellEstimator' && (
            <CordellEstimatorProductPackage
              coupon={coupon}
              onSelectPackage={onSelectPackage}
              productId={productId}
              productsConfig={product.productsConfig[FRIENDLY_NAME].configuration}
            />
          )}
        </div>
      )}
      {loading || renderLoading && (
        <div className='row spinner'>
          <i className='fa fa-spinner fa-pulse fa-3x fa-fw'></i>
          <span className='sr-only'>Loading...</span>
        </div>
      )}
    </div>
  );
};

PackageSelectionPage.propTypes = {
  location: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired
};

export default PackageSelectionPage;
